/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  model,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '@app/components/button';
import { DialogCloseRef } from '@app/components/dialog';
import {
  InputComponent,
  InputFieldSpacing,
  InputIcon,
} from '@app/components/input';
import { SpinnerComponent } from '@app/components/spinner';
import { TableComponent, TableConfig } from '@app/components/table';
import { TitleComponent } from '@app/components/title-section';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { AdminUsersService } from '@app/services/admin-users/admin-users.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { TranslateModule } from '@ngx-translate/core';
import { ChallengeBodyTeam, UserAaptivV2 } from '@skyfit/core-data-library';
import { orderBy } from 'lodash';

@Component({
  selector: 'app-challenge-users',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputComponent,
    TranslateModule,
    TableComponent,
    SpinnerComponent,
    TitleComponent,
    ButtonComponent,
  ],
  templateUrl: './challenge-users.component.html',
  styleUrl: './challenge-users.component.scss',
})
export class ChallengeUsersComponent implements OnInit, OnChanges {
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly usersService = inject(AdminUsersService);
  private readonly translateService = inject(TranslatorService);
  private readonly dialogCloseRef = inject(DialogCloseRef);
  private readonly destroyRef = inject(DestroyRef);

  private userCache: UserAaptivV2[] = [];

  teams = model<ChallengeBodyTeam[]>();
  selectedTeam = model<ChallengeBodyTeam>();
  InputIcon = InputIcon;
  searchInput = signal('');
  page = signal(0);
  users = signal([]);
  isLoading = signal(true);
  InputFieldSpacing = InputFieldSpacing;
  title = computed(() =>
    this.translateService.translate('challenges.addUsersTo', [
      this.selectedTeam()?.name,
    ])
  );
  filteredUsers = computed(() => {
    const searchInput = this.searchInput();
    const filteredUsers = this.users().filter((u) => {
      return (
        !searchInput ||
        this.isMatch(searchInput, u.name) ||
        this.isMatch(searchInput, u.email)
      );
    });
    return orderBy(filteredUsers, ['assignedTeam', 'name'], ['asc', 'asc']);
  });

  @Output()
  confirm = new EventEmitter<void>();

  @Output()
  cancel = new EventEmitter<void>();

  readonly tableConfig: TableConfig = {
    type: 'challenge',
    columns: [
      {
        key: 'name',
        width: '100px',
        label: 'challenges.userName',
        maxLength: 36,
      },
      {
        key: 'email',
        width: '180px',
        label: 'challenges.userEmail',
        maxLength: 36,
      },
      {
        key: 'assignedTeam',
        width: '100px',
        label: 'challenges.userTeam',
      },
    ],
  };

  ngOnInit(): void {
    this.getUsers();
    this.dialogCloseRef.close$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.searchInput.set('');
        this.page.set(0);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.selectedTeam &&
      changes.selectedTeam.currentValue !== changes.selectedTeam.previousValue
    ) {
      this.getUsers();
    }
  }

  getUsers(): void {
    this.isLoading.set(true);
    if (!this.userCache.length) {
      this.usersService
        .getUsers(this.adminTenantService.getTenantId())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((users) => {
          this.userCache = users.users;
          this.refreshUserData();
        });
    } else {
      this.refreshUserData();
    }
  }

  refreshUserData(): void {
    const userData = new Array<Record<string, any>>();

    for (const user of this.userCache) {
      const team = this.teams().find((t) => t.members.includes(user.userId));

      const record = { ...user } as Record<string, any>;

      record.assignedTeam = team?.name;
      record.selected = !!team?.name;
      record.disabled =
        record.selected && team?.name !== this.selectedTeam()?.name;

      userData.push(record);
    }

    this.users.set(userData);
    this.isLoading.set(false);
  }

  userSelected(user: { assignedTeam?: string }): void {
    user.assignedTeam = this.selectedTeam().name;
  }

  userUnselected(user: { assignedTeam?: string }): void {
    user.assignedTeam = '';
  }

  onConfirm(): void {
    const members: string[] = [];

    for (const user of this.users()) {
      if (user.selected && user.assignedTeam === this.selectedTeam().name) {
        members.push(user.userId);
      }
    }

    this.selectedTeam.update((t) => {
      t.members = members;
      return t;
    });

    this.searchInput.set('');
    this.page.set(0);
    this.confirm.emit();
  }

  onCancel(): void {
    this.searchInput.set('');
    this.page.set(0);
    this.cancel.emit();
  }

  private isMatch(input?: string, value?: string): boolean {
    return (
      input && value?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
    );
  }
}
